'use client';

import { initAll } from '@amplitude/unified';
import { detect } from 'detect-browser';
import { Fragment, useEffect } from 'react';

import env from 'config/env';
import { useTeacherContext, useUserContext } from 'context/Profile';

import { setInsightsUserId, setUserProperties } from './helpers';

/**
 * Keeps Efekta Insights' identity current independent of Amplitude init
 * (EFEKTA-18944) — `dev-tf` never initializes Amplitude (no API key), so
 * this can't live inside the Amplitude-gated effects below.
 */
const useInsightsIdentity = (userId: string | undefined) => {
  useEffect(() => {
    setInsightsUserId(userId ?? null);
  }, [userId]);
};

const browser = detect();

const initAmplitude = ({
  apiKey,
  userId,
  origin,
  school,
  center,
  role,
}: {
  apiKey: string;
  userId: string;
  origin: string;
  school?: string;
  center?: string;
  role?: string;
}) => {
  initAll(apiKey, {
    analytics: {
      userId,
    },
  });
  setUserProperties({
    Origin: origin,
    School: school,
    Center: center,
    Role: role,
    Resolution: `${window.screen.width}x${window.screen.height}`,
    Browser: browser?.name || 'unknown',
    Version: browser?.version || 'unknown',
    OS: browser?.os || 'unknown',
  });
};

export const useAmplitudeInitWithTeacher = () => {
  const { user } = useUserContext();
  const { selectedTeacher } = useTeacherContext();

  useInsightsIdentity(user?.id);

  useEffect(() => {
    if (env?.API_KEYS?.amplitude && !env.MOCKED && user?.id && selectedTeacher?.id) {
      initAmplitude({
        apiKey: env.API_KEYS.amplitude,
        userId: user.id,
        origin: window.location.origin,
        school: selectedTeacher?.department.school.name,
        center: selectedTeacher?.department.name,
        role: selectedTeacher?.role,
      });
    }
  }, [
    selectedTeacher?.department.name,
    selectedTeacher?.department.school.name,
    selectedTeacher?.id,
    selectedTeacher?.role,
    user?.id,
  ]);
};

export const AmplitudeInitTeacherfirst = () => {
  useAmplitudeInitWithTeacher();

  return <Fragment />;
};

export const useAmplitudeInitWithAdmin = () => {
  const { user } = useUserContext();

  useInsightsIdentity(user?.id);

  useEffect(() => {
    if (env?.API_KEYS?.amplitude && !env.MOCKED && user?.id) {
      initAmplitude({
        apiKey: env.API_KEYS.amplitude,
        userId: user.id,
        origin: window.location.origin,
      });
    }
  }, [user?.id]);
};

export const AmplitudeInitOpsportal = () => {
  useAmplitudeInitWithAdmin();

  return null;
};
